/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file saga.js
 * @description Role Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Services
import roleService from '@nephro/services/role-service';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Constants
import {
  CREATE_ROLE,
  CREATE_ROLE_PERMISSION,
  DELETE_ROLE_BY_ID,
  DELETE_ROLE_PERMISSION_BY_ID,
  GET_ALL_ROLES,
  GET_ROLE_BY_ID,
  UPDATE_ROLE
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get roles from backend
 * @param { object } action - Action with payload data
 */
export function* getRolesArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit } = data;

  try {
    const response = yield call(roleService.getRoles, paged, limit, page);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get role info by id
 * @param { object } action - Action with payload data
 */
export function* getRoleById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(roleService.getRoleById, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.roleNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to create role
 * @param { object } action - Action with payload data
 */
export function* createRole(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { name, description } = data;

  try {
    const response = yield call(roleService.createRole, name, description);
    yield put(
      setResponse('success', 'new rol', 'roleMg.rolCreated', response, callback)
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        `errors.${err.code}` || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to update role
 * @param { object } action - Action with payload data
 */
export function* updateRole(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { name, description, id } = data;

  try {
    const response = yield call(roleService.updateRole, name, description, id);
    yield put(
      setResponse(
        'success',
        'updated rol',
        'roleMg.rolUpdated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        `errors.${err.code}` || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to delete role by id
 * @param { object } action - Action with payload data
 */
export function* deleteRoleById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(roleService.deleteRoleById, id);
    yield put(
      setResponse(
        'success',
        'deleted rol',
        'roleMg.rolDeleted',
        { ...response },
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        `errors.${err.code}` || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to create role permission
 * @param { object } action - Action with payload data
 */
export function* createRolePermission(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { item_menu, permission, role } = data;

  try {
    const response = yield call(
      roleService.createRolePermission,
      item_menu,
      permission,
      role
    );
    yield put(setResponse('success', false, false, response, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        `errors.${err.code}` || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to delete role permission by id
 * @param { object } action - Action with payload data
 */
export function* deleteRolePermissionById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(roleService.deleteRolePermissionById, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        `errors.${err.code}` || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Declare all component sagas
 */
export function* watchRoleSaga() {
  yield takeLatest(GET_ALL_ROLES, getRolesArray);
  yield takeLatest(GET_ROLE_BY_ID, getRoleById);
  yield takeLatest(CREATE_ROLE, createRole);
  yield takeLatest(UPDATE_ROLE, updateRole);
  yield takeLatest(DELETE_ROLE_BY_ID, deleteRoleById);
  yield takeLatest(CREATE_ROLE_PERMISSION, createRolePermission);
  yield takeLatest(DELETE_ROLE_PERMISSION_BY_ID, deleteRolePermissionById);
}
